// "Model Bench has been updated — refresh". Every build writes version.json next to index.html (vite.config.ts,
// buildStamp); an open tab re-reads it and compares the build id with the one it was loaded from. The notes are
// worked out from CHANGELOG.md at build time (web/tools/releaseNotes.ts): they are shown only when their id differs
// from the notes this tab was built with, so the same news is not repeated.

export interface ReleaseNotes { id: string; title: string; items: string[] }
export interface Release { build: string; notes: ReleaseNotes | null }
/** What the banner shows: the newer build, and its notes when they are news to this tab. */
export interface UpdateBanner { build: string; notes: ReleaseNotes | null }

export const UPDATE_POLL_MS = 5 * 60_000;
export const DISMISSED_KEY = "modelbench.updateDismissed";

/** version.json as served, or null for anything else (an old release without the file serves index.html). */
export function parseRelease(data: unknown): Release | null {
  if (!data || typeof data !== "object") return null;
  const { build, notes } = data as { build?: unknown; notes?: unknown };
  if (typeof build !== "string" || !build) return null;
  const n = notes as Partial<ReleaseNotes> | null | undefined;
  const ok = !!n && typeof n.id === "string" && typeof n.title === "string" && Array.isArray(n.items) && n.items.every((i) => typeof i === "string");
  return { build, notes: ok ? { id: n.id!, title: n.title!, items: n.items! } : null };
}

/** The banner for the served release, or null when this tab is current or the person closed it for that build. */
export function bannerFor(current: { build: string; notesId: string }, served: Release | null, dismissed: string | null): UpdateBanner | null {
  if (!served || current.build === "dev" || served.build === current.build || served.build === dismissed) return null;
  return { build: served.build, notes: served.notes && served.notes.id !== current.notesId ? served.notes : null };
}

/** Reads version.json past every cache; null when it cannot be read. */
export async function fetchRelease(doFetch: typeof fetch = fetch): Promise<Release | null> {
  try {
    const res = await doFetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    return res.ok ? parseRelease(await res.json()) : null;
  } catch {
    return null;
  }
}
